export type Metrics = {
  inclusions: number | null;
  nights: number | null;
  revenue: number | null;
  orders: number | null;
  packages: number | null;
  alos: number | null;
  lead: number | null;
  upgrade: number | null;
  adr: number | null;
};
export type Group = Metrics & { name: string };
export type Pair = { name: string; current: number | null; comparison: number | null };
export type Variant = {
  currency: string;
  refund: string;
  period: string;
  to: string;
  cmpTo: string;
  windowCurrentRevenue: number | null;
  hasInclusions: boolean;
  currencyMismatch: boolean;
  effectiveCurrentStart: string;
  effectiveComparisonStart: string;
  inclusionDelta: number | null;
  current: Metrics;
  comparison: Metrics;
  curve: { day: number; current: number | null; comparison: number | null }[];
  roomNames: string[];
  roomCurve: ({ day: number } & Record<string, number | null>)[];
  rooms: { current: Group[]; comparison: Group[] };
  states: (Group & { country: string; state: string })[];
  countries: { name: string; region: string; current: Metrics; comparison: Metrics }[];
  dow: Pair[];
  dowBookings: Pair[];
  monthly: Pair[];
  leadtime: Pair[];
};
export type VendorData = {
  property: string;
  source: string;
  asOf: string;
  prepared: string;
  workbook: string;
  revision: string;
  current: { name: string; from: string; to: string };
  comparison: { name: string; from: string; to: string };
  variants: Variant[];
};
export const metricColumns: { key: keyof Metrics; label: string; digits: number; percent?: boolean }[] = [
  { key: 'nights', label: 'ROOM NIGHTS', digits: 0 },
  { key: 'revenue', label: 'VENDOR REVENUE (AR)', digits: 0 },
  { key: 'orders', label: 'BOOKINGS', digits: 0 },
  { key: 'packages', label: 'PACKAGES SOLD', digits: 0 },
  { key: 'alos', label: 'ALOS', digits: 2 },
  { key: 'lead', label: 'AVG. LEAD-TIME (DAYS)', digits: 0 },
  { key: 'upgrade', label: '% OF UPGRADES PURCHASED', digits: 2, percent: true },
  { key: 'adr', label: 'AVG. DAILY RATE', digits: 1 },
];
export const n = (v: number | null | undefined, digits = 0) =>
  typeof v === 'number' && Number.isFinite(v)
    ? new Intl.NumberFormat('en-AU', { maximumFractionDigits: digits, minimumFractionDigits: digits }).format(v)
    : 'Unknown';
export const pct = (v: number | null | undefined, digits = 1) =>
  typeof v === 'number' ? n(v * 100, digits) + '%' : 'Unknown';
export const share = (v: number | null, total: number | null) =>
  v !== null && total !== null && total !== 0 ? v / total : null;
export const change = (v: number | null, total: number | null) =>
  v !== null && total !== null && total !== 0 ? (v - total) / total : null;

/** Exact source scorecard zero guards and signed comparison denominators. */
export function vendorChange(key: keyof Metrics, current: number | null, comparison: number | null) {
  if (
    current === null ||
    comparison === null ||
    comparison === 0 ||
    (['revenue', 'packages'].includes(key) && current === 0)
  )
    return null;
  return key === 'upgrade' ? current - comparison : (current - comparison) / comparison;
}
